import React, { Suspense, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { TranslationProvider } from '@/context/TranslationContext';
import { SubscriptionProvider } from '@/components/PremiumGate';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Toaster } from '@/components/ui/sonner';
import { RouteFallback } from '@/components/RouteFallback';
import { DeferredWidgets } from '@/components/DeferredWidgets';
import { PublicFooterStrip } from '@/components/PublicFooterStrip';
import InviteGate from '@/components/InviteGate';
import { lazyRetry, prefetch, whenIdle } from '@/lib/lazyRetry';
import HomePage from '@/pages/public/HomePage';

// Every page except the homepage is its own chunk, downloaded on first visit (see lib/lazyRetry.js).
const loaders = {
  Dashboard: () => import('@/pages/Dashboard'),
  PreAuthValues: () => import('@/pages/PreAuthValues'),
  AuthPage: () => import('@/pages/AuthPage'),
};
const Day7EarningsNudge = lazyRetry(() => import('@/components/Day7EarningsNudge'));
const MembershipsPage = lazyRetry(() => import('@/pages/public/MembershipsPage'));
const DatingPage = lazyRetry(() => import('@/pages/public/DatingPage'));
const AuthPage = lazyRetry(loaders.AuthPage);
const VerifyEmailPage = lazyRetry(() => import('@/pages/VerifyEmailPage'));
const AuthCallback = lazyRetry(() => import('@/pages/AuthCallback'));
const PreAuthValues = lazyRetry(loaders.PreAuthValues);
const SharedArcPage = lazyRetry(() => import('@/pages/SharedArcPage'));
const Dashboard = lazyRetry(loaders.Dashboard);
const AssessmentHub = lazyRetry(() => import('@/pages/AssessmentHub'));
const AssessmentTaking = lazyRetry(() => import('@/pages/AssessmentTaking'));
const ResultsPage = lazyRetry(() => import('@/pages/ResultsPage'));
const CoupleCompare = lazyRetry(() => import('@/pages/CoupleCompare'));
const ProviderReport = lazyRetry(() => import('@/pages/ProviderReport'));
const RankingsPage = lazyRetry(() => import('@/pages/RankingsPage'));
const ChatRooms = lazyRetry(() => import('@/pages/ChatRooms'));
const ProfilePage = lazyRetry(() => import('@/pages/ProfilePage'));
const AccountSecurityPage = lazyRetry(() => import('@/pages/AccountSecurityPage'));
const NameAnalysis = lazyRetry(() => import('@/pages/NameAnalysis'));
const PortraitGallery = lazyRetry(() => import('@/pages/PortraitGallery'));
const PortraitCompare = lazyRetry(() => import('@/pages/PortraitCompare'));
const PublicPortraitCard = lazyRetry(() => import('@/pages/PublicPortraitCard'));
const PublicCompareCard = lazyRetry(() => import('@/pages/PublicCompareCard'));
const InviteRedirect = lazyRetry(() => import('@/pages/InviteRedirect'));
const WellnessPage = lazyRetry(() => import('@/pages/WellnessPage'));
const AIGuide = lazyRetry(() => import('@/pages/AIGuide'));
const FindResourcesPage = lazyRetry(() => import('@/pages/FindResourcesPage'));
const OnboardingFlow = lazyRetry(() => import('@/pages/OnboardingFlow'));
const ConnectionsPage = lazyRetry(() => import('@/pages/ConnectionsPage'));
const PremiumPage = lazyRetry(() => import('@/pages/PremiumPage'));
const InsightsForYou = lazyRetry(() => import('@/pages/InsightsForYou'));
const ContentManager = lazyRetry(() => import('@/pages/ContentManager'));
const PartnerDiscovery = lazyRetry(() => import('@/pages/PartnerDiscovery'));
const NotificationSettings = lazyRetry(() => import('@/pages/NotificationSettings'));
const TherapyHub = lazyRetry(() => import('@/pages/TherapyHub'));
const SessionBrief = lazyRetry(() => import('@/pages/SessionBrief'));
const TherapyChat = lazyRetry(() => import('@/pages/TherapyChat'));
const ResistanceJournal = lazyRetry(() => import('@/pages/ResistanceJournal'));
const SafeConnect = lazyRetry(() => import('@/pages/SafeConnect'));
const SafetySettings = lazyRetry(() => import('@/pages/SafetySettings'));
const ValuesAgreement = lazyRetry(() => import('@/pages/ValuesAgreement'));
const ShareStory = lazyRetry(() => import('@/pages/ShareStory'));
const CommunityDashboard = lazyRetry(() => import('@/pages/CommunityDashboard'));
const PractitionerDirectory = lazyRetry(() => import('@/pages/PractitionerDirectory'));
const PractitionerApply = lazyRetry(() => import('@/pages/PractitionerApply'));
const PractitionerProfile = lazyRetry(() => import('@/pages/PractitionerProfile'));
const PractitionerAvailability = lazyRetry(() => import('@/pages/PractitionerAvailability'));
const PractitionerDashboard = lazyRetry(() => import('@/pages/PractitionerDashboard'));
const PractitionerBillingPage = lazyRetry(() => import('@/pages/PractitionerBillingPage'));
const AdminPanel = lazyRetry(() => import('@/pages/AdminPanel'));
const AcceptInvitePage = lazyRetry(() => import('@/pages/AcceptInvitePage'));
const AdminSetup = lazyRetry(() => import('@/pages/AdminSetup'));
const ContactPage = lazyRetry(() => import('@/pages/ContactPage'));
const AboutPage = lazyRetry(() => import('@/pages/AboutPage'));
const SecurityPage = lazyRetry(() => import('@/pages/SecurityPage'));
const VsPage = lazyRetry(() => import('@/pages/VsPage'));
const WallOfClips = lazyRetry(() => import('@/pages/WallOfClips'));
const MessagingPage = lazyRetry(() => import('@/pages/MessagingPage'));
const MentalHealthLandscape = lazyRetry(() => import('@/pages/MentalHealthLandscape'));
const JourneyRecord = lazyRetry(() => import('@/pages/JourneyRecord'));
const MyMedia = lazyRetry(() => import('@/pages/MyMedia'));
const VideoJournal = lazyRetry(() => import('@/pages/VideoJournal'));
const ShareWithTherapist = lazyRetry(() => import('@/pages/ShareWithTherapist'));
const TherapistPortal = lazyRetry(() => import('@/pages/TherapistPortal'));
const TherapistDashboard = lazyRetry(() => import('@/pages/TherapistDashboard'));
const SocialSharePage = lazyRetry(() => import('@/pages/SocialSharePage'));
const RewardsPage = lazyRetry(() => import('@/pages/RewardsPage'));
const ListenerApplyPage = lazyRetry(() => import('@/pages/ListenerApplyPage'));
const TherapyRequest = lazyRetry(() => import('@/pages/TherapyRequest'));
const TherapyRequestDetail = lazyRetry(() => import('@/pages/TherapyRequestDetail'));
const SubscriptionManagement = lazyRetry(() => import('@/pages/SubscriptionManagement'));
const NotFoundPage = lazyRetry(() => import('@/pages/NotFoundPage'));
const LegalPage = lazyRetry(() => import('@/pages/LegalPage'));
const InvestorsPage = lazyRetry(() => import('@/pages/InvestorsPage'));
const ProvidersPage = lazyRetry(() => import('@/pages/ProvidersPage'));
const PressPage = lazyRetry(() => import('@/pages/PressPage'));
const CrisisSupportPage = lazyRetry(() => import('@/pages/CrisisSupportPage'));
const SupportInboxPage = lazyRetry(() => import('@/pages/SupportInboxPage'));
const SafetyGatePage = lazyRetry(() => import('@/pages/SafetyGatePage'));
const AssessmentHistoryPage = lazyRetry(() => import('@/pages/AssessmentHistoryPage'));
const CheckinSchedulePage = lazyRetry(() => import('@/pages/CheckinSchedulePage'));
const CheckinSessionPage = lazyRetry(() => import('@/pages/CheckinSessionPage'));
const SixtySecondResetPage = lazyRetry(() => import('@/pages/SixtySecondResetPage'));
const MyJourneyPage = lazyRetry(() => import('@/pages/MyJourneyPage'));
const MemoryPrivacyPage = lazyRetry(() => import('@/pages/MemoryPrivacyPage'));
const SahiriSessionsPage = lazyRetry(() => import('@/pages/SahiriSessionsPage'));
const SessionBookingPage = lazyRetry(() => import('@/pages/SessionBookingPage'));
const SessionDetailPage = lazyRetry(() => import('@/pages/SessionDetailPage'));
const SessionPaymentReturnPage = lazyRetry(() => import('@/pages/SessionPaymentReturnPage'));
const JourneySharePage = lazyRetry(() => import('@/pages/JourneySharePage'));
const JourneyViewPage = lazyRetry(() => import('@/pages/JourneyViewPage'));
const TrustSafetyPage = lazyRetry(() => import('@/pages/TrustSafetyPage'));
const PlansReturnPage = lazyRetry(() => import('@/pages/redesign/PlansReturnPage'));
const EligibilityPage = lazyRetry(() => import('@/pages/redesign/EligibilityPage'));
const GiftClaimPage = lazyRetry(() => import('@/pages/GiftClaimPage'));
const BlogPage = lazyRetry(() => import('@/pages/public/BlogPage'));
const MyAccessPage = lazyRetry(() => import('@/pages/redesign/MyAccessPage'));

/** Warm the most likely next page while the browser is idle: members → dashboard, visitors → values + sign-in. */
function usePrefetchNext() {
  const { user, loading } = useAuth();
  useEffect(() => {
    if (loading) return undefined;
    return whenIdle(() => {
      if (user) prefetch(loaders.Dashboard);
      else { prefetch(loaders.PreAuthValues); prefetch(loaders.AuthPage); }
    }, 3000);
  }, [user, loading]);
}

function AppRouter() {
  const location = useLocation();
  usePrefetchNext();

  // Check URL fragment for session_id (Google OAuth callback)
  // This must happen synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <Suspense fallback={<RouteFallback />}><AuthCallback /></Suspense>;
  }

  return (
    <>
      <Suspense fallback={null}><Day7EarningsNudge /></Suspense>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/dating" element={<DatingPage />} />
      <Route path="/gift/:token" element={<GiftClaimPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPage />} />
      <Route path="/values" element={<PreAuthValues />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/clips" element={<WallOfClips />} />
      <Route path="/arc/share/:token" element={<SharedArcPage />} />
      <Route path="/admin/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/assessments" element={<ProtectedRoute><AssessmentHub /></ProtectedRoute>} />
      <Route path="/assessments/history" element={<ProtectedRoute><AssessmentHistoryPage /></ProtectedRoute>} />
      <Route path="/assessment/:assessmentId" element={<ProtectedRoute><AssessmentTaking /></ProtectedRoute>} />
      <Route path="/results/:resultId" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
      <Route path="/results/:resultId/provider-report" element={<ProtectedRoute><ProviderReport /></ProtectedRoute>} />
      <Route path="/couple-compare" element={<ProtectedRoute><CoupleCompare /></ProtectedRoute>} />
      <Route path="/rankings" element={<ProtectedRoute><RankingsPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatRooms /></ProtectedRoute>} />
      <Route path="/chat/:roomId" element={<ProtectedRoute><ChatRooms /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/account/security" element={<ProtectedRoute><AccountSecurityPage /></ProtectedRoute>} />
      <Route path="/name-analysis" element={<ProtectedRoute><NameAnalysis /></ProtectedRoute>} />
      <Route path="/portrait-gallery" element={<ProtectedRoute><PortraitGallery /></ProtectedRoute>} />
      <Route path="/portrait-compare" element={<ProtectedRoute><PortraitCompare /></ProtectedRoute>} />
      <Route path="/portrait-card/:shareId" element={<PublicPortraitCard />} />
      <Route path="/compare/:compareId" element={<PublicCompareCard />} />
      <Route path="/invite" element={<InviteRedirect />} />
      <Route path="/wellness" element={<ProtectedRoute><WellnessPage /></ProtectedRoute>} />
      <Route path="/guide" element={<ProtectedRoute><AIGuide /></ProtectedRoute>} />
      <Route path="/resources/find" element={<ProtectedRoute><FindResourcesPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingFlow /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
      <Route path="/premium" element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
      <Route path="/premium/success" element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
      <Route path="/subscription/manage" element={<ProtectedRoute><SubscriptionManagement /></ProtectedRoute>} />
      <Route path="/pricing" element={<MembershipsPage />} />
      <Route path="/plans" element={<Navigate to="/pricing" replace />} />
      <Route path="/plans/return" element={<ProtectedRoute><PlansReturnPage /></ProtectedRoute>} />
      <Route path="/eligibility" element={<ProtectedRoute><EligibilityPage /></ProtectedRoute>} />
      <Route path="/my-access" element={<ProtectedRoute><MyAccessPage /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><InsightsForYou /></ProtectedRoute>} />
      <Route path="/content-manager" element={<ProtectedRoute><ContentManager /></ProtectedRoute>} />
      <Route path="/partner-discovery" element={<ProtectedRoute><PartnerDiscovery /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
      <Route path="/therapy" element={<ProtectedRoute><TherapyHub /></ProtectedRoute>} />
      <Route path="/therapy/brief" element={<ProtectedRoute><SessionBrief /></ProtectedRoute>} />
      <Route path="/therapy/chat" element={<ProtectedRoute><TherapyChat /></ProtectedRoute>} />
      <Route path="/therapy/request/:therapistId" element={<ProtectedRoute><TherapyRequest /></ProtectedRoute>} />
      <Route path="/therapy-request/:requestId" element={<ProtectedRoute><TherapyRequestDetail /></ProtectedRoute>} />
      <Route path="/resistance-journal" element={<ProtectedRoute><ResistanceJournal /></ProtectedRoute>} />
      <Route path="/safe-connect" element={<ProtectedRoute><SafeConnect /></ProtectedRoute>} />
      <Route path="/safety-settings" element={<ProtectedRoute><SafetySettings /></ProtectedRoute>} />
      <Route path="/values-agreement" element={<ProtectedRoute><ValuesAgreement /></ProtectedRoute>} />
      <Route path="/share-story" element={<ProtectedRoute><ShareStory /></ProtectedRoute>} />
      <Route path="/community-dashboard" element={<CommunityDashboard />} />
      <Route path="/practitioners" element={<PractitionerDirectory />} />
      <Route path="/practitioner-apply" element={<ProtectedRoute><PractitionerApply /></ProtectedRoute>} />
      <Route path="/practitioner-availability" element={<ProtectedRoute><PractitionerAvailability /></ProtectedRoute>} />
      <Route path="/practitioner-dashboard" element={<ProtectedRoute><PractitionerDashboard /></ProtectedRoute>} />
      <Route path="/practitioner-billing" element={<ProtectedRoute><PractitionerBillingPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin/safety/*" element={<ProtectedRoute><AdminPanel forcedTab="safety" /></ProtectedRoute>} />
      <Route path="/admin-setup" element={<AdminSetup />} />
      <Route path="/messages" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
      <Route path="/mental-health" element={<ProtectedRoute><MentalHealthLandscape /></ProtectedRoute>} />
      <Route path="/journey" element={<ProtectedRoute><JourneyRecord /></ProtectedRoute>} />
      <Route path="/my-media" element={<ProtectedRoute><MyMedia /></ProtectedRoute>} />
      <Route path="/video-journal" element={<ProtectedRoute><VideoJournal /></ProtectedRoute>} />
      <Route path="/share-with-therapist" element={<ProtectedRoute><ShareWithTherapist /></ProtectedRoute>} />
      <Route path="/therapist-view/:token" element={<TherapistPortal />} />
      <Route path="/therapist-dashboard/:token" element={<TherapistDashboard />} />
      <Route path="/share" element={<ProtectedRoute><SocialSharePage /></ProtectedRoute>} />
      <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
      <Route path="/support/inbox" element={<ProtectedRoute><SupportInboxPage /></ProtectedRoute>} />
      <Route path="/support/inbox/:conversationId" element={<ProtectedRoute><SupportInboxPage /></ProtectedRoute>} />
      <Route path="/listener/apply" element={<ProtectedRoute><ListenerApplyPage /></ProtectedRoute>} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/about-founder" element={<Navigate to="/about" replace />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/trust-safety" element={<TrustSafetyPage />} />
      {/* Commonly guessed marketing URLs — send people somewhere useful instead of a 404 (audit D2.4) */}
      <Route path="/features" element={<Navigate to={{ pathname: '/', hash: '#free' }} replace />} />
      <Route path="/customers" element={<Navigate to="/about" replace />} />
      <Route path="/community-standards" element={<Navigate to="/trust-safety" replace />} />
      <Route path="/vs/:slug" element={<VsPage />} />
      <Route path="/for-investors" element={<InviteGate audience="investors"><InvestorsPage /></InviteGate>} />
      <Route path="/for-providers" element={<InviteGate audience="providers"><ProvidersPage /></InviteGate>} />
      <Route path="/for-press" element={<InviteGate audience="press"><PressPage /></InviteGate>} />
      <Route path="/crisis-support" element={<CrisisSupportPage />} />
      <Route path="/safety/phq9-item9" element={<ProtectedRoute><SafetyGatePage /></ProtectedRoute>} />
      <Route path="/checkin/schedule" element={<ProtectedRoute><CheckinSchedulePage /></ProtectedRoute>} />
      <Route path="/checkin/session/:sessionId" element={<ProtectedRoute><CheckinSessionPage /></ProtectedRoute>} />
      <Route path="/checkin/reset" element={<ProtectedRoute><SixtySecondResetPage /></ProtectedRoute>} />
      <Route path="/my-journey" element={<ProtectedRoute><MyJourneyPage /></ProtectedRoute>} />
      <Route path="/my-journey/share" element={<ProtectedRoute><JourneySharePage /></ProtectedRoute>} />
      <Route path="/memory-privacy" element={<ProtectedRoute><MemoryPrivacyPage /></ProtectedRoute>} />
      <Route path="/sessions" element={<ProtectedRoute><SahiriSessionsPage /></ProtectedRoute>} />
      <Route path="/sessions/book" element={<ProtectedRoute><SessionBookingPage /></ProtectedRoute>} />
      <Route path="/sessions/payment/return" element={<ProtectedRoute><SessionPaymentReturnPage /></ProtectedRoute>} />
      <Route path="/sessions/:bookingId" element={<ProtectedRoute><SessionDetailPage /></ProtectedRoute>} />
      <Route path="/journey-view/:token" element={<JourneyViewPage />} />
      <Route path="/practitioner/:id" element={<PractitionerProfile />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
      </Suspense>
      <PublicFooterStrip />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SubscriptionProvider>
          <TranslationProvider>
          {/* Global mood-responsive ambient glow */}
          <div className="fixed inset-0 pointer-events-none z-0 transition-all" style={{ background: 'var(--mood-glow, none)', transitionDuration: '2s' }} />
          <div className="relative z-10">
            <AppRouter />
          </div>
          <DeferredWidgets />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
              },
            }}
          />
        </TranslationProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
